// Origin-aware navigation for the standalone event edit page.
//
// Entering edit is a full-page Turbo `<a href>` — the edit page is its own
// Rails mount, not part of the events SPA — so the opener tags the edit URL
// with its own location as a `from` query param. The edit header reads `from`
// to render an in-app "back to the list you came from" affordance and to point
// it at the exact list URL (filters + page live in the query string).

const editPath = (orderId: number, eventId: number | string): string =>
  `/orders/${orderId}/events/${eventId}/edit`;

/**
 * Build the event edit path, tagging it with `from` so the edit page can
 * navigate back to the opener. `from` is URL-encoded because it is itself a
 * path-with-query (e.g. `/events?tab=upcoming&page=2`).
 */
export function buildEventEditPath(
  orderId: number,
  eventId: number | string,
  from?: string | null,
): string {
  const base = editPath(orderId, eventId);
  const safe = sanitizeInternalPath(from);
  return safe ? `${base}?from=${encodeURIComponent(safe)}` : base;
}

/**
 * Accept only same-origin relative paths (`/foo`). Reject protocol-relative
 * (`//evil.com`), absolute URLs, and empty values — a crafted `from` must not
 * become an open redirect when rendered as an `<a href>`.
 */
export function sanitizeInternalPath(path: string | null | undefined): string | null {
  if (!path) return null;
  if (!path.startsWith('/')) return null;
  // Reject protocol-relative (`//host`) and backslash variants (`/\host`,
  // `\\host`). Browsers normalise `\` to `/`, so these resolve to an external
  // origin when used as an `<a href>` (open redirect). A real internal path
  // never contains a literal backslash (a `\` in a query string arrives
  // percent-encoded as `%5C`).
  if (path.startsWith('//')) return null;
  if (path.includes('\\')) return null;
  return path;
}

/** Read + sanitize the `from` origin out of a location search string. */
export function backTargetFromSearch(search: string): string | null {
  return sanitizeInternalPath(new URLSearchParams(search).get('from'));
}

/** Whether a resolved back target points at the events list (trailing slash tolerated). */
export function isEventsListPath(path: string): boolean {
  // Normalise a trailing slash on the list segment (`/events/` → `/events`,
  // `/events/?page=2` → `/events?page=2`) so a trailing-slash origin still
  // resolves to the events list. Does not touch `/events/123` (a sub-path).
  const p = path.replace(/^\/events\/(?=$|[?#])/, '/events');
  return p === '/events' || p.startsWith('/events?') || p.startsWith('/events#');
}

/** The opener's current location (`pathname + search`), or null outside a browser. */
export function currentLocationPath(): string | null {
  if (typeof window === 'undefined') return null;
  return window.location.pathname + window.location.search;
}
